import api from '../../../api/apiClient';
import { capitalizeString } from '../../shared/utils/misc';
import { calculateEmploymentPeriod, calculateEmploymentPeriodFromDates } from './employees.utils';

const unwrap = async (promise) => {
  const res = await promise;
  return res.data;
};

export const getAllEmployees = async () => {
  const { data: raw } = await unwrap(await api.get('/api/hr/employees'));

  const normalized = raw.map((emp) => {
    const isActive = emp.employment_period?.includes('Present');

    return {
      id: emp.id,
      employeeId: String(emp.employee_id),
      name: emp.name,
      contact: emp.contact,
      email: emp.email,
      department: capitalizeString(emp.department),
      designation: emp.designation,
      status: isActive ? 'active' : 'inactive',
      employmentPeriod: calculateEmploymentPeriod(emp.employment_period),
    };
  });

  return {
    active: normalized.filter((e) => e.status === 'active'),
    inactive: normalized.filter((e) => e.status === 'inactive'),
  };
};

/** `GET /api/hr/employee/:id` and `PUT /api/hr/employee/:id` answer in the same shape. */
const normalizeEmployeeDetails = (raw) => ({
  id: raw.id,
  employeeId: raw.employee_id,
  name: raw.name,
  gender: raw.gender?.toLowerCase() ?? null,
  // Age is derived from this on the card; the details payload must carry `date_of_birth`.
  dateOfBirth: raw.date_of_birth ?? null,
  contact: raw.contact ?? null,
  email: raw.email,
  designation: raw.designation ?? null,
  department: raw.department ? capitalizeString(raw.department) : null,
  departmentId: raw.department_id ?? null,
  nationality: raw.nationality ?? null,
  medicalCondition: raw.medical_condition ?? null,
  aadhaarNumber: raw.aadhaar_number ?? null,
  panNumber: raw.pan_number ?? null,
  status: raw.employment_status?.toLowerCase() ?? 'active',
  employmentPeriod: calculateEmploymentPeriodFromDates(raw.joining_date, raw.leaving_date),
  currentSalary: raw.current_salary ? Number(raw.current_salary) : null,
  // The API sends the contract file's URL as a plain string, not a file object.
  resumeUrl: raw.resume ?? null,
  leavingReason: raw.leaving_reason ?? null,
  payslips: Array.isArray(raw.payslip) ? raw.payslip : [],
});

export const getEmployeeDetails = async (id) => {
  if (!id) throw new Error('Employee id is required to fetch employee data');
  const { data: raw } = await unwrap(await api.get(`/api/hr/employee/${id}`));

  return normalizeEmployeeDetails(raw);
};

/**
 * Saves the employee edit form. Only the keys the form owns are sent - the API patches the
 * columns it is given and leaves the rest (joining date, employment status, documents) alone.
 */
export const updateEmployeeDetails = async (id, values) => {
  if (!id) throw new Error('Employee id is required to update employee data');

  const text = (value) => (typeof value === 'string' ? value.trim() : (value ?? null)) || null;

  const payload = {
    name: text(values.name),
    gender: text(values.gender),
    contact: text(values.contact),
    email: text(values.email),
    designation: text(values.designation),
    department_id: values.departmentId || null,
    nationality: text(values.nationality),
    medical_condition: text(values.medicalCondition),
    aadhaar_number: text(values.aadhaarNumber),
    pan_number: text(values.panNumber),
    current_salary: text(values.currentSalary),
  };

  const { data: raw } = await unwrap(await api.put(`/api/hr/employee/${id}`, payload));

  return normalizeEmployeeDetails(raw);
};

/** Department options for the edit form's select - `GET /api/departments` answers `{ data: [] }`. */
export const getDepartmentOptions = async () => {
  const { data: raw } = await unwrap(await api.get('/api/departments'));

  return (Array.isArray(raw) ? raw : []).map((department) => ({
    value: department.id,
    label: capitalizeString(department.name),
  }));
};

export const adminResetUserPassword = async (id) => {
  if (!id) throw new Error('Employee id is required to reset password');

  const { data: raw } = await unwrap(await api.put(`/api/hr/get-password/${id}`));

  return {
    employeeId: raw.employee_id,
    username: raw.username,
    newPassword: raw.new_password,
  };
};
